import { useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { Loader } from '@components/Loader';
import { DetailsCell } from '@insights_module/components/integrations/DetailsCell';
import { StringCell } from '@insights_module/components/integrations/StringCell';
import { CodeCell } from '@insights_module/components/integrations/CodeCell';
import { NoTagsFallback } from '@insights_module/components/integrations/NoTagsFallback';
import { NoSearchResults } from '@insights_module/components/integrations/NoSearchResults';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { ActionCell } from '@insights_module/components/integrations/ActionCell';
import { CollectionCard } from '@insights_module/components/integrations/CollectionCard';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { LineLoader } from '@components/LineLoader';

import { useIntegrationTags, TagType } from '@insights_module/hooks/useIntegrationTags';
import { useNotification } from '@insights_module/hooks/useNotification';
import {
  ColumnType,
  ColumnKeys,
  TagRowType,
  TagActionCellType,
  TagDetailsCellType,
} from '@insights_module/types/Megaphone';
import { IntegrationType } from '@insights_module/types/Integrations';
import {
  INTEGRATION_EPISODE_LIMIT,
  INTEGRATION_TITLE_MAPPER,
  INTEGRATION_TYPE,
} from '@insights_module/constants/integrations';

import { getIntegrationFormattedDate } from '@utils/dateUtils';
import { TrackingPixelTableModal } from '@insights_module/components/integrations/TrackingPixelTableModal';
import { AddTrackingPixelModal } from '@insights_module/components/integrations/AddTrackingPixelModal';
import { objectToToken } from '@utils/commonUtils';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { INSIGHTS_QUERY_KEY } from '@insights_module/constants/queryKeys';
import { useUser } from '@auth_module/hooks/useUser';
import { useOrganizationUsers } from '@account_module/hooks/useOrganizationUsers';
import { IOrganizationUser } from '@account_module/types/Organizations';
import { CSV_EXPORT_TYPE, useCSVExport } from '@insights_module/hooks/useCSVExport';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { useDebouncedState } from '@app/hooks/useDebounce';

const HEADER_HEIGHT = '93';

const Container = tw.div`relative w-full h-auto max-h-[calc(100vh - ${String(
  HEADER_HEIGHT
)}px)] bg-page-background p-[24px]`;
const TableScrollWrapper = tw.div`relative w-full h-auto max-h-[calc(100vh - 180px)]  p-0 overflow-y-auto border border-solid rounded-[8px] border-separate border-gray-1`;
const Table = tw.table`relative w-full h-full bg-page-background overflow-auto`;
const GridContainer = tw.div`grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 grid-flow-row gap-[13.5px]`;
const THead = tw.thead`w-full bg-neutral-background sticky top-0 z-[1]`;
const TBody = tw.tbody`w-full`;
const TR = tw.tr`w-full border-b border-solid border-gray-1 py-1`;
const TD = tw.td`p-2 first:px-0 last:pr-0 text-[11px] cursor-default text-text-and-icon`;
const Checkbox = tw.button`w-[13px] h-[13px] border border-solid border-text-and-icon-muted rounded-sm flex items-center justify-center p-0 text-neutral-background`;
const Flex = tw.div`flex items-center justify-start gap-2 whitespace-nowrap px-4`;
const TableEnd = tw.tr`w-full h-[1px]`;

const COLUMN_KEYS = {
  DETAILS: 'details' as ColumnKeys,
  ELIGIBLE_EPISODES: 'eligible_episodes' as ColumnKeys,
  ACTIVE_IN_INTEGRATION: 'active_in_integration' as ColumnKeys,
  ACTIVE_EPISODE_DATE_RANGE: 'active_episode_publish_date_range' as ColumnKeys,
  DESCRIPTION: 'description' as ColumnKeys,
  CODE: 'code' as ColumnKeys,
  ACTION: 'action' as ColumnKeys,
};

type DetailsHeadColumnType = {
  data: TagRowType[] | undefined;
  setSelectedRows: (item: string[]) => void;
  selectedRows: string[];
};

const LoadingRow = ({ isLoading }: { isLoading: boolean | undefined }): JSX.Element => (
  <TR
    css={[tw`transition-all duration-200 no-scrollbar`, isLoading ? tw`h-[60px]` : tw`h-0 hidden`]}
    data-testid='load-more-row'>
    <TD colSpan={Object.keys(COLUMN_KEYS).length} align='center'>
      <Loader size='md' theme='dark' />
    </TD>
  </TR>
);

const DetailsHeadColumn = ({ data = [], setSelectedRows, selectedRows }: DetailsHeadColumnType) => {
  const { t } = useTranslation();
  const isSomeRowSelected = useMemo(
    () => selectedRows.length > 0 && selectedRows.length < data?.length,
    [selectedRows]
  );
  const isAllRowsSelected = useMemo(
    () => selectedRows.length === data?.length,
    [selectedRows, data]
  );

  const handleChange = () => {
    if (!data.length) {
      return;
    }

    if (isAllRowsSelected || isSomeRowSelected) {
      setSelectedRows([]);
      return;
    }

    setSelectedRows(data.map((item: TagRowType) => item.action.tag_id));
  };

  const CheckIcon = useMemo(() => {
    if (isSomeRowSelected && selectedRows.length < data?.length) {
      return '-';
    }

    if (selectedRows.length === data?.length) {
      return <CheckMarkIcon tw='stroke-neutral-background w-2 h-2' />;
    }

    if (!selectedRows.length) {
      return '';
    }
  }, [selectedRows]);

  return (
    <Flex>
      <Checkbox
        onClick={handleChange}
        css={[
          (isSomeRowSelected || isAllRowsSelected) &&
            data.length &&
            tw`bg-accent-muted border-accent-muted`,
        ]}>
        {CheckIcon}
      </Checkbox>
      {t('insights.integrations.table.head_item.collection_name')}
    </Flex>
  );
};

function getColumns(type: IntegrationType, data?: TagRowType[]): ColumnType[] {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedTableType } = useInsightsContext();
  const { organization } = useUser();
  const { currentUser } = useOrganizationUsers(organization?.id);

  const {
    selectedRows,
    selectedAction,
    setSelectedAction,
    setSelectedRows,
    setIsEditTagModalOpen,
    setTagToEdit,
    setTagToDelete,
    setIsDeleteTagModalOpen,
    setIsDescriptionModalOpen,
    setTagToDuplicate,
    setIsCreateTagModalOpen,
  } = useIntegrationsContext();

  const { isEnabled: isExportShowSummaryEnabled } = useFeatureFlag(
    FEATURE_FLAG.EXPORT_SHOW_SUMMARY_FROM_EPISODE_LIST
  );
  const { initiateCSVExport, inProgress: isExportShowSummaryInProgress } = useCSVExport(
    CSV_EXPORT_TYPE.AUDIOS_GROUPED_BY_GROUP
  );
  const [tagIdsToExport, setTagIdsToExport] = useState<string[]>([]);

  const [copiedTag, setCopiedTag] = useState<string | null>(null);
  const { notify, NTFC_TYPE } = useNotification();

  const integrationTitle = INTEGRATION_TITLE_MAPPER[type];

  const handleDuplicateTag = (id: string) => {
    setTagToDuplicate(id);
    setIsCreateTagModalOpen(true);
    setSelectedAction(null);
  };

  const handleDeleteTag = (id: string) => {
    setTagToDelete(id);
    setIsDeleteTagModalOpen(true);
  };

  const handleApplyTagToInsights = (id: string) => {
    const tag = data?.find((item) => item.action.id === id);
    if (!tag) return;

    const queryToken = objectToToken(tag.aql_query);
    navigate(`/insights/episodes?query_token=${queryToken}`);
    queryClient.invalidateQueries([INSIGHTS_QUERY_KEY.infinite_episodes]);
    setSelectedTableType('episodes');
  };

  const handleCopyToClipboard = async (val: string | null) => {
    setCopiedTag(val);

    if (val) {
      try {
        await navigator.clipboard.writeText(val);

        notify({
          type: NTFC_TYPE.INFO,
          message: t('common.copied_to_clipboard'),
          duration: 1000,
        });
      } catch (err) {
        console.error('Failed to copy value to clipboard: ', err);
      }
    }
  };

  const handleExportShowSummary = (id: string) => {
    const tag = data?.find((item) => item.action.id === id);
    if (!tag) return;

    setTagIdsToExport([...tagIdsToExport, id]);
    initiateCSVExport(tag.aql_query, () => {
      setTagIdsToExport((prev) => prev.filter((tagId) => tagId !== id));
    });
  };

  return [
    {
      key: COLUMN_KEYS.DETAILS,
      header: (
        <DetailsHeadColumn
          data={data}
          setSelectedRows={setSelectedRows}
          selectedRows={selectedRows}
        />
      ),
      align: 'left',
      columnRenderer: (item) =>
        DetailsCell(item as TagDetailsCellType, setSelectedRows, selectedRows),
    },
    {
      key: COLUMN_KEYS.ELIGIBLE_EPISODES,
      header: t('insights.integrations.table.head_item.total_eligible_episodes') as string,
      align: 'left',
      columnRenderer: (item) => StringCell(item as string),
    },
    {
      key: COLUMN_KEYS.ACTIVE_IN_INTEGRATION,
      header: `Active in ${integrationTitle}`,
      align: 'left',
      columnRenderer: (item) => StringCell(item as string),
    },
    {
      key: COLUMN_KEYS.ACTIVE_EPISODE_DATE_RANGE,
      header: t(
        'insights.integrations.table.head_item.active_episode_publish_date_range'
      ) as string,
      align: 'left',
      columnRenderer: (item) => StringCell(item as string),
    },
    {
      key: COLUMN_KEYS.CODE,
      header: t('insights.integrations.table.head_item.code') as string,
      columnRenderer: (item) =>
        CodeCell(item as keyof TagRowType, copiedTag, handleCopyToClipboard),
    },
    {
      key: COLUMN_KEYS.ACTION,
      header: '',
      align: 'right',
      columnRenderer: (item) =>
        ActionCell(
          type,
          item as TagActionCellType,
          selectedAction,
          currentUser as IOrganizationUser,
          tagIdsToExport,
          setSelectedAction,
          handleCopyToClipboard,
          setIsDescriptionModalOpen,
          handleDeleteTag,
          handleApplyTagToInsights,
          setIsEditTagModalOpen,
          setTagToEdit,
          handleDuplicateTag,
          isExportShowSummaryEnabled,
          isExportShowSummaryInProgress,
          handleExportShowSummary,
          t
        ),
    },
  ];
}

export const TagsList = ({ type }: { type: IntegrationType }) => {
  const { searchTerm, layoutType, selectedAction, setSelectedAction } = useIntegrationsContext();
  const debouncedSearchTerm = useDebouncedState(
    () => (searchTerm.trim().length > 1 ? searchTerm.trim() : ''),
    300
  );
  const {
    tags,
    isFetchingNextPage,
    isLoading,
    isFetching,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    tagsDetails,
  } = useIntegrationTags(type, debouncedSearchTerm);

  const endOfTableRef = useRef<HTMLTableRowElement>(null);
  const tableScrollWrapperRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  const triggerFetchMore = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (!isLoading && entry.isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
    },
    [isLoading, hasNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!endOfTableRef.current || !tableScrollWrapperRef.current) return;

    const fetchMoreObserver = new IntersectionObserver(triggerFetchMore, {
      root: tableScrollWrapperRef.current,
      rootMargin: '50px',
      threshold: 0.1,
    });
    fetchMoreObserver.observe(endOfTableRef.current);

    return () => {
      if (endOfTableRef.current) {
        fetchMoreObserver.unobserve(endOfTableRef.current);
      }
    };
  }, [triggerFetchMore]);

  const listData: TagRowType[] = useMemo(() => {
    if (!tags || !tags.length) {
      return [];
    }

    const tagsAggregatedData = tags?.map((tag: TagType) => {
      const tagEpisodesData = tagsDetails.find((item) => item.data?.id === tag.id);
      const totalEpisodesCount = tagEpisodesData?.data?.total || 0;
      const oldestEpisode = tagEpisodesData?.data?.oldestEpisode;
      const latestEpisode = tagEpisodesData?.data?.latestEpisode;

      // in moment when we have tags data but not tag details, is loading is undefined and we should treat that as we are loading more data
      const isMoreDataLoading =
        typeof tagEpisodesData?.isLoading === 'undefined' ? true : tagEpisodesData.isLoading;

      const publishedEpisodesDateRange = `${getIntegrationFormattedDate(oldestEpisode?.published_date)} - ${getIntegrationFormattedDate(latestEpisode?.published_date)}`;

      const activeInIntegration = isMoreDataLoading ? (
        <Loader size='sm' theme='dark' tw='border-gray-3' />
      ) : totalEpisodesCount < INTEGRATION_EPISODE_LIMIT[type] ? (
        totalEpisodesCount
      ) : (
        INTEGRATION_EPISODE_LIMIT[type]
      );

      return {
        details: {
          id: tag?.id,
          tag_id: tag?.query.id,
          title: tag?.query.display_name,
          description: tag?.query?.description || null,
        },
        eligible_episodes: isMoreDataLoading ? (
          <Loader size='sm' theme='dark' tw='border-gray-3' />
        ) : (
          tagEpisodesData?.data?.total || 0
        ),
        active_in_integration: activeInIntegration,
        active_episode_publish_date_range: isMoreDataLoading ? (
          <Loader size='sm' theme='dark' tw='border-gray-3' />
        ) : (
          publishedEpisodesDateRange
        ),
        code: tag?.query.id,
        action: {
          id: tag?.id,
          tag_id: tag?.query.id,
          title: tag?.query.display_name,
          episode_count: tagEpisodesData?.data?.total || 0,
        },
        aql_query: tag?.query.aql_query,
      };
    });

    return tagsAggregatedData;
  }, [tags, tagsDetails, type]);

  const cols =
    type === INTEGRATION_TYPE.IHM
      ? getColumns(type, listData).filter((item) => item.key !== COLUMN_KEYS.ACTIVE_IN_INTEGRATION)
      : getColumns(type, listData);

  const rowCols =
    type === INTEGRATION_TYPE.IHM
      ? tw`grid-cols-[minmax(200px,_1fr)_1fr_1fr_1fr_1fr_1fr]`
      : tw`grid-cols-[minmax(200px,_1fr)_1fr_1fr_1fr_1fr_1fr_1fr]`;

  const listFallback = useMemo(
    () => (searchTerm ? <NoSearchResults /> : <NoTagsFallback />),
    [searchTerm]
  );

  interface ExtendedEventTarget extends HTMLElement {
    parentNode: ExtendedEventTarget;
  }

  const documentClickHandler = useCallback(
    (e: MouseEvent<ExtendedEventTarget, MouseEvent>): void => {
      const actionBtnCls = 'integration-action-btn';
      const target = e.target as ExtendedEventTarget;

      const getElClass = (classList: DOMTokenList | undefined): string[] =>
        Array.from(classList || []);

      const isClassIncluded = (el: ExtendedEventTarget, str: string): boolean =>
        getElClass(el?.classList).includes(str);

      const isActionComponent =
        isClassIncluded(target, actionBtnCls) ||
        isClassIncluded(target?.parentNode, actionBtnCls) ||
        isClassIncluded(target?.parentNode?.parentNode, actionBtnCls);

      if (!isActionComponent) {
        setSelectedAction(null);
      }
    },
    [selectedAction]
  );

  // Add a click eventListener for closing row actions cell menu
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    document.addEventListener('click', (e: any) => documentClickHandler(e));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return () => document.removeEventListener('click', (e: any) => documentClickHandler(e));
  }, []);

  if (isInitialLoading) {
    return <Loader theme='dark' size='md' />;
  }

  if (!listData.length) return <Container>{listFallback}</Container>;

  return (
    <Container>
      {layoutType === 'grid' ? (
        <GridContainer data-testid='integrations-grid-container'>
          <LineLoader
            loading={!!searchTerm.length && isFetching}
            tw='w-full absolute left-0 top-0 z-[10] h-[1px]'
          />
          {listData.map((item) => (
            <CollectionCard key={item.details.id} item={item} integrationType={type} />
          ))}
        </GridContainer>
      ) : (
        <TableScrollWrapper ref={tableScrollWrapperRef}>
          <Table ref={tableRef} data-testid='integrations-table'>
            <THead>
              <TR css={[rowCols]}>
                {cols?.map((column: ColumnType) => (
                  <TD
                    tw='text-text-and-icon-muted text-[11px] font-semibold whitespace-nowrap'
                    key={column.key}
                    style={{ textAlign: column.align || ('left' as never) }}
                    title={typeof column.header === 'string' ? column.header : ''}>
                    {column.header}
                  </TD>
                ))}
                <TD colSpan={cols.length}>
                  <LineLoader
                    loading={!!searchTerm.length && isFetching}
                    tw='w-full absolute left-0 top-0 z-[10] h-[1px]'
                  />
                </TD>
              </TR>
            </THead>
            <TBody>
              {listData?.map((item, index) => {
                return (
                  <TR css={[rowCols, tw`odd:bg-gray-2 even:bg-neutral-background`]} key={index}>
                    {cols?.map((column, index) => (
                      <TD
                        key={index}
                        css={[
                          { textAlign: column?.align as never },
                          column.key !== COLUMN_KEYS.ACTION && tw`overflow-hidden text-ellipsis`,
                        ]}>
                        {column.columnRenderer
                          ? column.columnRenderer(item[column.key as keyof TagRowType])
                          : String(item[column.key as keyof TagRowType])}
                      </TD>
                    ))}
                  </TR>
                );
              })}
              <LoadingRow isLoading={isFetchingNextPage} />
              <TableEnd ref={endOfTableRef} />
            </TBody>
          </Table>
        </TableScrollWrapper>
      )}
      <TrackingPixelTableModal />
      <AddTrackingPixelModal />
    </Container>
  );
};
